import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CreatePeriodMaster, PeriodMaster, UpdatePeriodMaster } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['periods', companyId] as const;

export function usePeriods(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () => api<PeriodMaster[]>(`/companies/${companyId}/periods?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function usePeriod(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['periods', companyId, id],
    queryFn: () => api<PeriodMaster>(`/companies/${companyId}/periods/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreatePeriod(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreatePeriodMaster, 'companyId'>) =>
      apiJson<typeof input, PeriodMaster>('POST', `/companies/${companyId}/periods`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdatePeriod(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePeriodMaster }) =>
      apiJson<UpdatePeriodMaster, PeriodMaster>(
        'PATCH',
        `/companies/${companyId}/periods/${id}`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeletePeriod(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, PeriodMaster>('DELETE', `/companies/${companyId}/periods/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
